import { useEffect, useRef, useState } from 'react';
import { OVERLAY_HISTORY_KEY } from './use-back-to-close';

export function intakeJobFromHistory(state: unknown): string | null {
    if (!state || typeof state !== 'object') return null;
    const entry = state as { page?: unknown; intakeJobId?: unknown };
    return entry.page === 'intake' && typeof entry.intakeJobId === 'string'
        && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(entry.intakeJobId)
        ? entry.intakeJobId : null;
}

/** Intake's register/detail entries share the app's existing {page,stage} history. */
export function useIntakeJobNavigation() {
    const [selectedJobId, setSelectedJobId] = useState<string | null>(() => intakeJobFromHistory(window.history.state));
    const previousSelection = useRef(selectedJobId);

    useEffect(() => {
        const onPop = (event: PopStateEvent) => {
            if (event.state?.page !== 'intake') return;
            const next = intakeJobFromHistory(event.state);
            previousSelection.current = next;
            setSelectedJobId(next);
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    useEffect(() => {
        const previous = previousSelection.current;
        if (previous === selectedJobId) return;
        previousSelection.current = selectedJobId;
        const current = window.history.state || {};
        // An in-app Back consumes the same detail entry as browser Back. It
        // must not push a new register entry that traps Back in a detail loop.
        if (!selectedJobId && current.page === 'intake' && current.intakeDetailEntry
            && intakeJobFromHistory(current) === previous) {
            window.history.back();
            return;
        }
        const { intakeJobId: _job, intakeDetailEntry: _detail, [OVERLAY_HISTORY_KEY]: overlay, ...base } = current;
        const state = selectedJobId
            ? { ...base, page: 'intake', intakeJobId: selectedJobId, intakeDetailEntry: true }
            : { ...base, page: 'intake' };
        // A job opened from an overlay (New RFQ's create, or "Open existing
        // job") takes the overlay's entry, so Back from the job does not stop
        // on the closed overlay first.
        if (selectedJobId && !previous && !overlay) window.history.pushState(state, '', window.location.href);
        else window.history.replaceState(state, '', window.location.href);
    }, [selectedJobId]);

    return [selectedJobId, setSelectedJobId] as const;
}
